import React, { useEffect, useState } from "react";
import { Table, Tag, Button, Space, Modal, Input, Empty } from "antd";
import { getRewardsSummaryApi, getRewardsTransactionsApi } from "../../services/api";
import StatCard from "./StatCard";

const { Search } = Input;

const LEVEL_COLORS = {
  "Beginner Parent": "default",
  "Caring Parent": "blue",
  "Growth Champion": "green",
  "Super Parent": "purple",
  "Parenting Star": "gold",
  "Kidodom Legend": "magenta",
};

export default function Rewards() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [loadingTx, setLoadingTx] = useState(false);

  const fetchRows = async () => {
    const res = await getRewardsSummaryApi();
    setRows(res.data.data || []);
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openTransactions = async (row) => {
    setSelected(row);
    setOpen(true);
    setTransactions(null);
    setLoadingTx(true);
    try {
      const res = await getRewardsTransactionsApi(row.userId);
      setTransactions(res.data.data || { xpTransactions: [], rewardTransactions: [] });
    } finally {
      setLoadingTx(false);
    }
  };

  const filteredRows = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
  });

  const totalXp = rows.reduce((sum, r) => sum + (r.totalXp || 0), 0);
  const totalPoints = rows.reduce((sum, r) => sum + (r.totalRewardPoints || 0), 0);
  const totalRedeemableValue = rows.reduce((sum, r) => sum + (r.redeemableValue || 0), 0);

  const columns = [
    {
      title: "Customer",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v || "-"}</div>
          <div style={{ fontSize: 11, color: "#888" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Level",
      dataIndex: "level",
      render: (v) => <Tag color={LEVEL_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "XP",
      dataIndex: "totalXp",
      sorter: (a, b) => a.totalXp - b.totalXp,
      defaultSortOrder: "descend",
      render: (v) => (v || 0).toLocaleString(),
    },
    {
      title: "Reward Points",
      dataIndex: "totalRewardPoints",
      sorter: (a, b) => a.totalRewardPoints - b.totalRewardPoints,
      render: (v) => (v || 0).toLocaleString(),
    },
    {
      title: "Redeemable Value",
      dataIndex: "redeemableValue",
      render: (v) => `₹${(v || 0).toLocaleString()}`,
    },
    {
      title: "",
      render: (_, r) => (
        <Button size="small" onClick={() => openTransactions(r)}>
          View History
        </Button>
      ),
    },
  ];

  const txColumns = (unit) => [
    {
      title: "Event",
      dataIndex: "eventType",
    },
    {
      title: "Amount",
      dataIndex: unit,
      render: (v) => (
        <span style={{ color: v < 0 ? "#e24b4a" : "#1d9e75", fontWeight: 500 }}>
          {v > 0 ? "+" : ""}
          {v}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">XP & Rewards</div>
          <div className="page-sub">{rows.length} customers with recorded activity</div>
        </div>
        <Search
          placeholder="Search by name or email…"
          style={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 16 }}>
        <StatCard label="TOTAL XP EARNED" value={totalXp.toLocaleString()} color="#534ab7" />
        <StatCard label="REWARD POINTS OUTSTANDING" value={totalPoints.toLocaleString()} color="#1E88E5" />
        <StatCard label="OUTSTANDING LIABILITY" value={`₹${totalRedeemableValue.toLocaleString()}`} color="#e24b4a" />
      </div>

      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="userId"
        size="small"
      />

      {/* ================= TRANSACTION HISTORY MODAL ================= */}
      <Modal
        open={open}
        title={selected ? `${selected.name || selected.email} — Transaction History` : "Transaction History"}
        footer={null}
        width={720}
        onCancel={() => setOpen(false)}
      >
        {transactions && (
          <div style={{ display: "grid", gap: 20 }}>
            <div>
              <div className="section-title">XP Transactions</div>
              {transactions.xpTransactions?.length ? (
                <Table
                  columns={txColumns("xp")}
                  dataSource={transactions.xpTransactions}
                  rowKey={(_, i) => `xp-${i}`}
                  size="small"
                  pagination={{ pageSize: 5 }}
                />
              ) : (
                <Empty description="No XP transactions" />
              )}
            </div>
            <div>
              <div className="section-title">Reward Points Transactions</div>
              {transactions.rewardTransactions?.length ? (
                <Table
                  columns={txColumns("points")}
                  dataSource={transactions.rewardTransactions}
                  rowKey={(_, i) => `pts-${i}`}
                  size="small"
                  pagination={{ pageSize: 5 }}
                />
              ) : (
                <Empty description="No reward points transactions" />
              )}
            </div>
          </div>
        )}
        {loadingTx && !transactions && <div>Loading…</div>}
      </Modal>
    </div>
  );
}
